import { Component, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { InvestmentDetails } from '../../models/investment-details';
import { OwnershipDialogComponent } from '../../components/ownership-dialog/ownership-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import { OwnershipModel } from '../../models/ownership-model';
import { IFormComponent } from '../../models/i-form-component';
import { LookupItem } from '../../models/lookup-item';
import { map, Subject, takeUntil } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';

@Component({
  selector: 'app-business-investments',
  templateUrl: './business-investments.component.html',
  styleUrl: './business-investments.component.scss'
})
export class BusinessInvestmentsComponent implements OnInit, IFormComponent {
  @Input() model!: InitialAssessmentForm;
  @Input() countries!: LookupItem[];
  @Input() industries!: LookupItem[];
  @Input() ownershipInterests!: LookupItem[];
  @Input() assetAmounts!: LookupItem[];

  displayedColumns = ['name', 'country', 'industry', 'position', 'edit', 'delete'];
  ownershipDataSource = new MatTableDataSource<OwnershipModel>([]);

  submitAttempted: boolean = false;

  formBuilder = inject(NonNullableFormBuilder);

  investmentForm = this.formBuilder.group({
    hadOwnership: ['']
  },{
    validators: requiredListWhenYesValidator([
                {
                    controlName: 'hadOwnership',
                  errorKey: 'ownershipsRequired',
                  getItems: () => this.model?.investment?.ownerships ?? []
                }])
  });

  constructor(private dialog: MatDialog) { }

      private destroy$ = new Subject<void>();
      private breakpointObserver = inject(BreakpointObserver);
      isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
        map(result => result.matches),
        takeUntil(this.destroy$)
      );
    
    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }
  

  ngOnInit(): void {

    this.model = this.model || new InitialAssessmentForm();
    this.model.investment = this.model.investment || new InvestmentDetails();
    this.ownershipDataSource.data = this.model.investment!.ownerships;

    // Bind form to model data
    this.investmentForm.patchValue(this.model.investment);

    // Subscribe to form changes to update model
    this.investmentForm.valueChanges.subscribe(value => {
      this.model.investment = {
        ...this.model.investment!,
        ...value
      }
    });
  }

  refreshOwnershipTable(): void {
    this.ownershipDataSource.data = [...this.model.investment!.ownerships];
  }

  add(): void {
    const dialogRef = this.dialog.open(OwnershipDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { model: null, countries: this.countries, industries: this.industries, ownershipInterests: this.ownershipInterests, assetAmounts: this.assetAmounts }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.investment!.ownerships.push(result);
        this.refreshOwnershipTable();
        this.investmentForm.controls.hadOwnership.updateValueAndValidity();
      }
    });
  }

  remove(index: number): void {
    this.model.investment!.ownerships.splice(index, 1);
    this.refreshOwnershipTable();
    this.investmentForm.controls.hadOwnership.updateValueAndValidity();
  }

  edit(index: number): void {
    const ownership = this.model.investment!.ownerships[index];
    const dialogRef = this.dialog.open(OwnershipDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { model: ownership, countries: this.countries, industries: this.industries, ownershipInterests: this.ownershipInterests, assetAmounts: this.assetAmounts }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.investment!.ownerships[index] = result;
        this.refreshOwnershipTable();
        this.investmentForm.controls.hadOwnership.updateValueAndValidity();
      }
    });
  }

  isFormValid(): boolean {
    this.submitAttempted = true;
    return this.investmentForm.valid;
  }

  markFormAsTouched(): void {
    this.investmentForm.markAllAsTouched();
  }

}
